import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import {
  WEATHER_SUN,
  WEATHER_MOON,
  WEATHER_DROP,
  WEATHER_UMBRELLA,
} from '@bwoty-web/ui-kit/icons';

import './weatherChartMonth.scss';

const WeatherChartMonth = ({
  month,
  day = '-',
  night = '-',
  waterTemp = null,
  sunshineHoursPerDay = '-',
  rainFreeDays = '-',
  texts,
  active = false,
}) => {
  const isGlobe = useIsGlobetrotter();

  const dayTempClasses = classNames('gw-weather-chart-month__degree', {
    hidden: !day,
  });

  const nightTempClasses = classNames('gw-weather-chart-month__degree', {
    hidden: !night,
  });

  const renderDegrees = (value, classes) => {
    if (value === undefined || value === null) {
      return <span className="gw-weather-chart-month__fallback">-</span>;
    }
    return (
      <>
        {value}
        <span className={classes}>°</span>
      </>
    );
  };

  return (
    <div
      className={classNames('gw-weather-chart-month', {
        'gw-weather-chart-month--active': active,
      })}
    >
      <h3 className="gw-weather-chart-month__name">{month}</h3>
      <ul className="visually-hidden">
        {day != null && (
          <li>
            {texts.dayTempText}: {day}°
          </li>
        )}
        {night != null && (
          <li>
            {texts.nightTempText}: {night}°
          </li>
        )}
        <li>
          {texts.sunHoursPerDayText}: {sunshineHoursPerDay}
        </li>
        {waterTemp && (
          <li>
            {texts.waterTempText}: {waterTemp}°
          </li>
        )}
        <li>
          {texts.rainFreeDaysText}: {rainFreeDays}
        </li>
      </ul>
      <div className="gw-weather-chart-month__visual" aria-hidden="true">
        <div className="gw-weather-chart-month__temperatures">
          <div className="gw-weather-chart-month__temperature">
            <Icon
              className="gw-weather-chart-month__sun-icon"
              path={WEATHER_SUN}
            />
            {renderDegrees(day, dayTempClasses)}
          </div>
          <div className="gw-weather-chart-month__temperature gw-weather-chart-month__temperature--night">
            <Icon
              className="gw-weather-chart-month__moon-icon"
              path={WEATHER_MOON}
              size="xs"
            />
            {renderDegrees(night, nightTempClasses)}
          </div>
        </div>
        <div className="gw-weather-chart-month__extra-entry">
          <div className="gw-weather-chart-month__icon-wrapper">
            {!isGlobe && (
              <Icon
                className="gw-weather-chart-month__sun-icon"
                path={WEATHER_SUN}
                size="xs"
              />
            )}
            {sunshineHoursPerDay}
          </div>
          <p>{texts.sunHoursPerDayText}</p>
        </div>
        {waterTemp && (
          <div className="gw-weather-chart-month__extra-entry">
            <div className="gw-weather-chart-month__icon-wrapper">
              {!isGlobe && (
                <Icon
                  className="gw-weather-chart-month__drop-icon"
                  path={WEATHER_DROP}
                  size="xs"
                />
              )}
              <span className="gw-weather-chart-month__degree">
                {waterTemp}°
              </span>
            </div>
            <p>{texts.waterTempText}</p>
          </div>
        )}
        <div className="gw-weather-chart-month__extra-entry">
          <div className="gw-weather-chart-month__icon-wrapper">
            {!isGlobe && (
              <Icon
                className="gw-weather-chart-month__umbrella-icon"
                path={WEATHER_UMBRELLA}
                size="xs"
              />
            )}
            {rainFreeDays}
          </div>
          <p>{texts.rainFreeDaysText}</p>
        </div>
      </div>
    </div>
  );
};

WeatherChartMonth.propTypes = {
  active: PropTypes.bool,
  month: PropTypes.string.isRequired,
  day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  night: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  waterTemp: PropTypes.number,
  sunshineHoursPerDay: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  rainFreeDays: PropTypes.number,
  texts: PropTypes.shape({
    dayTempText: PropTypes.string,
    nightTempText: PropTypes.string,
    waterTempText: PropTypes.string.isRequired,
    sunHoursPerDayText: PropTypes.string.isRequired,
    rainFreeDaysText: PropTypes.string.isRequired,
  }).isRequired,
};

export default WeatherChartMonth;
